/* eslint-disable max-lines */
import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';

import { merge, Observable, Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';

import type { CurrentUser, User, UserBase } from '@recruitee/account-types';
import type { Attachment } from '@recruitee/attachments-types';
import { filterBoolean } from '@recruitee/common';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { Guest, Note, NoteId } from '@recruitee/notes-types';
import { ChecklistPluginChecklistChange, ConfigEvents } from '@recruitee/prosemirror';
import { RenderDirective, WysiwygContext, WysiwygContext$ } from '@recruitee/prosemirror/angular';
import {
  ProsemirrorDefaultConfig,
  ProsemirrorDefaultConfigDirective,
} from '@recruitee/prosemirror/webapp';
import { NoteReactionsContainerComponent } from '@recruitee/reactions';
import {
  AttachmentItemComponent,
  BadgeComponent,
  ButtonComponent,
  ConnectedOverlayDirective,
  DateComponent,
  DialogService,
  DividerComponent,
  DropdownComponent,
  DropdownItemComponent,
  DropdownWidth,
  GroupComponent,
  IconButtonComponent,
  IconColorDirective,
  IconComponent,
  OverlayOriginDirective,
  RtStatefulComponent,
  RtTooltip,
  ThreadExpanderComponent,
} from '@recruitee/user-interface';
import { generateUuid, LifeCycle } from '@recruitee/utils';
import { VisibilitySelectorContainerComponent } from '@recruitee/visibility';
import { VisibilityLevel } from '@recruitee/visibility-types';

import { NoteAvatarComponent } from '../note-avatar/note-avatar.component';
import { NOTE_AVATAR_UPDATE_STRATEGY } from '../note-avatar/note-avatar.types';
import { RtNoteAvatarUpdate } from '../note-avatar/strategy/rt-note-avatar-update';
import { NoteFormComponent } from '../note-form/note-form.component';
import { NoteTypingIndicatorMessageComponent } from '../note-typing-indicator-message/note-typing-indicator-message.component';

type ReplyNote = {
  id?: number | string;
  text?: string;
};

type State = {
  author: CurrentUser | Guest | User | null;
  authorName: string;
  editing: boolean;
  replying: boolean | null;
  replyNote: ReplyNote | null;
  replyTempId: number | string;
  note: Partial<Note>;
  isPrivate: boolean;
  isVisibilityEnabled: boolean;
};

@Component({
  selector: 'rt-note-item',
  templateUrl: './note-item.component.html',
  styleUrls: ['./note-item.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.context-private]': 'this.state.isPrivate && !noteIsReply',
    '[class]': 'withNewStyling ? "new" : "old"',
  },
  providers: [
    { provide: WysiwygContext$, useFactory: () => new WysiwygContext$(null) },
    { provide: NOTE_AVATAR_UPDATE_STRATEGY, useClass: RtNoteAvatarUpdate },
  ],
  standalone: true,
  imports: [
    DividerComponent,
    NoteAvatarComponent,
    BadgeComponent,
    GroupComponent,
    IconButtonComponent,
    OverlayOriginDirective,
    RtTooltip,
    ConnectedOverlayDirective,
    DropdownComponent,
    DropdownItemComponent,
    IconColorDirective,
    IconComponent,
    DateComponent,
    AttachmentItemComponent,
    ButtonComponent,
    NoteFormComponent,
    NoteTypingIndicatorMessageComponent,
    ThreadExpanderComponent,
    TranslatePipe,
    NgTemplateOutlet,
    NoteReactionsContainerComponent,
    VisibilitySelectorContainerComponent,
    RenderDirective,
    ProsemirrorDefaultConfigDirective,
  ],
})
export class NoteItemComponent
  extends RtStatefulComponent<State>
  implements OnChanges, OnInit, OnDestroy
{
  @Input()
  public set note(note: Partial<Note>) {
    this.setState(
      {
        ...this.state,
        note,
      },
      false,
    );
  }
  public get note(): Partial<Note> {
    return this.state.note;
  }
  @Input() public privateContext: boolean = false;
  @Input() public currentAdmin?: CurrentUser;
  @Input() public cachedReply: { replyToId: number; value: string };
  @Input() public canDeleteAttachments: boolean = false;
  @Input() public canHighLevelDataAccess: boolean = false;
  @Input()
  public currentlyTypedNotes: Array<{
    admin: UserBase;
    replyToId?: number | null;
  }> = [];

  @Input() public editing: boolean = false;
  @Input() public isVisibilityEnabled: boolean = false;
  @Input() public replying: boolean;

  @Input() public noteSaveLoading: Array<string | number> = [];
  @Input() public notesDeleteLoading: Array<string | number> = [];
  @Input() public noteAttachmentLoading: { [key: string]: string[] };
  @Input() public notesBeingEdited: Array<string | number> = [];
  @Input() public notesBeingRepliedTo: Array<string | number> = [];
  @Input() public allNotesFlat: Note[];

  @Input() public adminsList: User[];
  @Input() public disallowedAdminsList: User[];

  @Input() public attachmentsTemp: Record<string, Array<{ uuid: string; attachment: Attachment }>>;

  @Input() public noteIsReply = false;

  @Input() public isReadOnly = false;

  @Input() public withNewStyling = false;
  @Input() public hasConnector = false;

  @Output() public replyTypingStart: EventEmitter<{ replyToId: NoteId }> = new EventEmitter();
  @Output() public replyTypingFinish: EventEmitter<void> = new EventEmitter();

  @Output() public noteDiscardClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteEditClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteReplyClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteSaveClick: EventEmitter<Partial<Note>> = new EventEmitter();
  @Output() public noteChecklistClick: EventEmitter<{ id: number; bodyJson: object }> =
    new EventEmitter();
  @Output() public noteDeleteClick: EventEmitter<NoteId> = new EventEmitter();
  @Output() public notePinClick: EventEmitter<NoteId> = new EventEmitter();
  @Output() public noteUnpinClick: EventEmitter<NoteId> = new EventEmitter();
  @Output()
  public replyChanges: EventEmitter<{ replyToId: number; value: string }> = new EventEmitter();
  @Output()
  public uploadAttachmentClick: EventEmitter<{
    attachment: { file: File };
    note: Note;
  }> = new EventEmitter();
  @Output()
  public deleteAttachmentClick: EventEmitter<{
    attachmentId: number;
    note?: Note;
  }> = new EventEmitter();

  @LifeCycle('onDestroy')
  public ngOnDestroy$: Observable<void>;
  public rendererChanged$ = new Subject<void>();

  public VisibilityLevel: typeof VisibilityLevel = VisibilityLevel;
  public ProsemirrorConfig = ProsemirrorDefaultConfig;
  public DropdownWidth = DropdownWidth;

  public noteItemIsHovered: boolean = false;
  public dropdownIsOpened: boolean = false;
  public allRepliesVisible = false;

  @ViewChild('noteRenderer', { static: false })
  private set noteRenderer(
    value: RenderDirective<InstanceType<NoteItemComponent['ProsemirrorConfig']>>,
  ) {
    if (value === this._noteRenderer) return;

    this._noteRenderer = value;
    this.rendererChanged$.next();
    this.setCheckboxEdit(value);
  }

  private get unknownAuthor(): boolean {
    return !this.note.triggered && !this.state.author;
  }

  public _noteRenderer: RenderDirective<InstanceType<NoteItemComponent['ProsemirrorConfig']>>;

  constructor(
    private i18n: I18nService,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private renderer: Renderer2,
    private elRef: ElementRef,
    private wysiwygContext$: WysiwygContext$,
    protected cdRef: ChangeDetectorRef,
  ) {
    super(cdRef);

    this.createState({
      author: null,
      authorName: '',
      editing: false,
      replying: null,
      replyNote: {},
      replyTempId: generateUuid(),
      note: {},
      isVisibilityEnabled: false,
      isPrivate: false,
    });
  }

  public trackByNote(index: number, item: Note) {
    return item.id;
  }

  public trackById<T extends { id: number }>(index: number, item: T): number {
    return item.id;
  }

  public ngOnInit(): void {
    if (this.note.admin && !this.note.admin.anonymizedAt) {
      this.setState({
        ...this.state,
        author: this.note.admin,
        authorName: `${this.note.admin.firstName} ${this.note.admin.lastName}`,
      });
    } else if (this.note.guest) {
      this.setState({
        ...this.state,
        author: this.note.guest,
        authorName: this.note.guest.name,
      });
    } else if (this.unknownAuthor) {
      this.setState({
        ...this.state,
        authorName: this.i18n.translate('app.common.deleted_user'),
      });
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.note) {
      this.setWysiwygContext();
      this.setState(
        {
          ...this.state,
          isPrivate: this.getPrivate(),
          isVisibilityEnabled: this.isVisibilityEnabled,
        },
        false,
      );
    }

    if (changes.replying?.currentValue) {
      const isReplyCached = this.cachedReply && this.note.id === this.cachedReply.replyToId;

      this.setState(
        {
          ...this.state,
          replyNote:
            this.currentAdmin && this.note.admin && this.note.admin.id !== this.currentAdmin?.id
              ? {
                  text: isReplyCached
                    ? this.cachedReply.value
                    : `@[${this.getAuthorName(this.note)}](admin:${this.note.admin.id}) `,
                  id: this.state.replyTempId,
                }
              : {
                  id: this.state.replyTempId,
                  ...(isReplyCached ? { text: this.cachedReply.value } : {}),
                },
        },
        false,
      );
    }
  }

  public ngOnDestroy(): void {
    if (this.replying) {
      this.replyTypingFinish.emit();
    }
    this.rendererChanged$.next();
    this.rendererChanged$.complete();
  }

  public getAuthorName(note: Partial<Note>): string {
    if (note && note.admin) {
      return `${note.admin.firstName} ${note.admin.lastName}`;
    } else if (note && note.guest) {
      return note.guest.name;
    }
    return '';
  }

  public get typingAdmins(): UserBase[] {
    return this.currentlyTypedNotes
      .filter(note => note.replyToId === this.note.id)
      .map(note => note.admin);
  }

  public get showHeaderButtons(): boolean {
    if (this.noteItemIsHovered || this.dropdownIsOpened) {
      if (this.isEditable() || this.isDeletable() || (!this.note.pinnedAt && !this.noteIsReply)) {
        return true;
      }
      if (this.isEditable() && this.isDeletable() && this.note.pinnedAt) {
        return true;
      }
    }
    return false;
  }

  public isEditable(): boolean {
    return !!this.currentAdmin && this.note.adminId === this.currentAdmin.id;
  }

  public isDeletable(): boolean {
    return this.canHighLevelDataAccess || this.isEditable();
  }

  public showReply(): void {
    this.noteReplyClick.emit(this.note);
    this.replyTypingStart.emit({ replyToId: this.note.id! });
  }

  public showEdit(): void {
    this.noteEditClick.emit(this.note);
    this.replyTypingFinish.emit();
  }

  public cancel(): void {
    this.noteDiscardClick.emit(this.note);
    this.replyTypingFinish.emit();
    this.changeNoteVisibilityClass(this.state.isPrivate);
  }

  public changeNoteVisibilityClass(isPrivate: boolean): void {
    isPrivate
      ? this.renderer.addClass(this.elRef.nativeElement, 'context-private')
      : this.renderer.removeClass(this.elRef.nativeElement, 'context-private');
  }

  public toggleDropdown(dropdown: ConnectedOverlayDirective): void {
    this.dropdownIsOpened = !dropdown.open;
    dropdown.open = !dropdown.open;
    this.cdRef.detectChanges();
  }

  public deleteNote(): void {
    this.dialogService
      .error(
        {
          title: this.i18n.translate('app.candidate.confirmation.delete.title'),
          message: this.i18n.translate('app.candidate.confirmation.delete.message'),
          agreeLabel: this.i18n.translate('app.candidate.confirmation.delete.agree_label'),
        },
        this.viewRef,
      )
      .pipe(take(1), filterBoolean)
      .subscribe(() => {
        this.noteDeleteClick.emit(this.note.id!);
      });
  }

  public pinNote(): void {
    this.notePinClick.emit(this.note.id!);
    // eslint-disable-next-line max-lines
  }

  public unpinNote(): void {
    this.noteUnpinClick.emit(this.note.id!);
  }

  public setHeaderButtonsVisibility(visible: boolean): void {
    this.noteItemIsHovered = visible;
    this.cdRef.detectChanges();
  }

  public showAllReplies(): void {
    this.allRepliesVisible = true;
    this.cdRef.detectChanges();
  }

  public onWysiwygEvent(event: ConfigEvents<InstanceType<NoteItemComponent['ProsemirrorConfig']>>) {
    if (event instanceof ChecklistPluginChecklistChange) {
      const { doc } = event.state;
      const { id } = this.note;
      this.noteChecklistClick.emit({ id: <number>id, bodyJson: { doc } });
    }
  }

  private getPrivate(): boolean {
    return this.note.visibility?.level !== VisibilityLevel.Public;
  }

  private setCheckboxEdit(
    noteRenderer: RenderDirective<InstanceType<NoteItemComponent['ProsemirrorConfig']>>,
  ) {
    if (!noteRenderer) return;

    noteRenderer.publicApi$
      .pipe(filterBoolean, takeUntil(merge(this.ngOnDestroy$, this.rendererChanged$)))
      .subscribe(({ checklistService, mentionsService }) => {
        const isOwner = this.note.adminId === this.currentAdmin?.id;
        const isAutomatedActionNote = !this.note.adminId && !this.note.guestId;
        checklistService.setCanEdit(isOwner || isAutomatedActionNote);
      });
  }

  private setWysiwygContext() {
    let owner: WysiwygContext['owner'] = null;
    if (this.note.admin) {
      owner = {
        name: `${this.note.admin.firstName} ${this.note.admin.lastName}`,
      };
    } else if (this.note.guest) {
      owner = {
        name: this.note.guest.name,
      };
    }
    this.wysiwygContext$.next({ owner });
  }
}
